// components/auth/ForcePasswordChangeDialog.tsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Typography,
    InputAdornment,
    IconButton,
    Box
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { passwordAPI } from '../../utils/api/passwordAPI';
import logger from '../../utils/logging/logger';

const componentName = 'ForcePasswordChangeDialog';

interface ForcePasswordChangeDialogProps {
    open: boolean;
    userId: number;
    userName: string;
    onPasswordChanged: () => void;
    onLogout: () => void;
}

const ForcePasswordChangeDialog: React.FC<ForcePasswordChangeDialogProps> = ({
    open,
    userId,
    userName,
    onPasswordChanged,
    onLogout
}) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const validatePassword = (): string | null => {
        if (!newPassword.trim()) {
            return 'Password is required';
        }

        if (newPassword.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        // Check for number or special character
        const hasNumberOrSpecial = /[\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
        if (!hasNumberOrSpecial) {
            return 'Password must contain at least one number or special character';
        }

        if (newPassword !== confirmPassword) {
            return 'Passwords do not match';
        }

        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationError = validatePassword();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            logger.log('LogAuth', componentName, 'User changing forced password', { userId });

            // Set the new password (with mustChangePassword = false to clear the flag)
            // The stored procedure s_SetUserPassword will set MustChangePassword = 0
            const success = await passwordAPI.setPassword(userId, newPassword, false);

            if (success) {
                logger.log('LogAuth', componentName, 'Password changed successfully', { userId });
                onPasswordChanged();
            } else {
                setError('Failed to change password. Please try again.');
            }
        } catch (err: unknown) {
            logger.log('LogError', componentName, 'Error changing password', err);

            // Provide more specific error messages
            if (err instanceof Error) {
                if (err.message.includes('Network') || err.message.includes('fetch')) {
                    setError('Network error. Please check your connection and try again.');
                } else if (err.message.includes('too short') || err.message.includes('characters')) {
                    setError('Password does not meet requirements. Please use at least 8 characters with a number or special character.');
                } else {
                    setError('An error occurred while changing your password. Please try again.');
                }
            } else {
                setError('An error occurred while changing your password. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            slotProps={{
                backdrop: {
                    onClick: (e) => e.stopPropagation()
                }
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    Password Change Required
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Your account requires a password change before you can continue.
                                Please set a new password below.
                            </Typography>
                        </Alert>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Logged in as: <strong>{userName}</strong>
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                            mb: 2,
                            // Hide browser's native password reveal button
                            '& input::-ms-reveal, & input::-ms-clear': { display: 'none' },
                            '& input::-webkit-credentials-auto-fill-button': { display: 'none' }
                        }}
                        helperText="8+ characters, include a number or special character"
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            disabled={isSubmitting}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isSubmitting}
                        sx={{
                            // Hide browser's native password reveal button
                            '& input::-ms-reveal, & input::-ms-clear': { display: 'none' },
                            '& input::-webkit-credentials-auto-fill-button': { display: 'none' }
                        }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            disabled={isSubmitting}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={onLogout}
                        disabled={isSubmitting}
                    >
                        Logout
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !newPassword.trim() || !confirmPassword.trim()}
                        sx={{ minWidth: 150 }}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : 'Change Password'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ForcePasswordChangeDialog;
